/**
 * @mentions in a comment (components/collab/CommentsPanel.jsx): finding the "@lu" being typed, putting the picked
 * member in the text, and the members a text still mentions. Members come from vfs-service's `/access`
 * (`{ owner, members }`), each `{ email, name?, sub? }`.
 */

/** Everyone in the project who can be mentioned (the owner first), without `you`, as `{ email, name, label }`. */
export function mentionables(access, you = null) {
  const people = [access?.owner, ...(access?.members || [])].filter((p) => p && p.email);
  const seen = new Set();
  const out = [];
  people.forEach((person) => {
    const email = String(person.email).toLowerCase();
    if (seen.has(email)) { return; }
    seen.add(email);
    if (you && ((you.sub && person.sub === you.sub) || (you.email && String(you.email).toLowerCase() === email))) { return; }
    const name = person.name || email.split('@')[0];
    out.push({ email, name, label: name.split(/\s+/)[0].replace(/[^\p{L}\p{N}._-]/gu, '') || email.split('@')[0] });
  });
  return out;
}

/** The "@word" right before the caret: `{ query, start }` (start: the index of "@"), or null. */
export function mentionQuery(text, caret) {
  const before = String(text || '').slice(0, caret);
  const match = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(before);
  if (!match) { return null; }
  return { query: match[2], start: before.length - match[2].length - 1 };
}

/** Members matching what is typed after "@" (name or email), at most `max`. */
export function matchMentions(people, query, max = 6) {
  const q = String(query || '').toLowerCase();
  return people.filter((p) => !q || p.name.toLowerCase().includes(q) || p.email.includes(q) || p.label.toLowerCase().startsWith(q)).slice(0, max);
}

/** The text with the "@query" at `start` replaced by "@Label ", and the caret after it. */
export function insertMention(text, start, caret, person) {
  const value = String(text || '');
  const inserted = `@${person.label} `;
  return { text: `${value.slice(0, start)}${inserted}${value.slice(caret)}`, caret: start + inserted.length };
}

/** The emails of the picked people whose "@Label" is still in the text. */
export function mentionedEmails(text, picked) {
  const value = String(text || '');
  return [...new Set((picked || []).filter((p) => new RegExp(`(^|\\s)@${p.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\p{L}\\p{N}])`, 'u').test(value)).map((p) => p.email))];
}

/** A comment's text split into parts, the @mentions marked: `[{ text, mention: bool }]`. */
export function mentionParts(body, mentions = []) {
  const labels = (mentions || [])
    .map((m) => String(m.name || m.email || '').split(/\s+/)[0].split('@')[0])
    .filter(Boolean)
    .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const value = String(body || '');
  if (labels.length === 0) { return [{ text: value, mention: false }]; }
  // With one capturing group, split puts the matches at the odd indexes.
  const pattern = new RegExp(`(@(?:${labels.join('|')}))`, 'iu');
  return value.split(pattern).map((part, index) => ({ text: part, mention: index % 2 === 1 })).filter((part) => part.text !== '');
}
